<div class="relative flex flex-row h-full w-full" aria-labelledby="modal-title" role="dialog" aria-modal="true">

    <div class="absolute inset-0 z-10 overflow-hidden green-noise-bg flex items-center justify-center">

        <!-- ── Glassmorphism close button ────────────────────────────────── -->
        <button type="button" (click)="closeVideoFeedback()" aria-label="Close"
            class="absolute top-3 left-3 z-20 flex items-center justify-center size-9 bg-white/10 hover:bg-white/20 active:bg-white/15 backdrop-blur-md rounded-full border border-white/20 text-white/90 transition-all duration-150 shadow-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                stroke="currentColor" class="size-4">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
        </button>

        <div class="relative h-full w-full blackout-card-container flex flex-col overflow-hidden p-2 gap-y-2">
            <div class="grid grid-cols-5 gap-x-2 h-full w-full">

                <!-- ── Left: Video player ─────────────────────────────────── -->
                <article class="col-span-3 relative overflow-hidden">
                    @if (canNavigateVideos()) {
                    <button type="button" (click)="goToPreviousVideo()" [disabled]="!canGoToPreviousVideo()"
                        aria-label="Previous draft"
                        class="absolute left-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white/90 shadow-sm backdrop-blur-md transition-all duration-150 hover:bg-white/20 active:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/10">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                            stroke="currentColor" aria-hidden="true" class="size-5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
                        </svg>
                    </button>
                    <button type="button" (click)="goToNextVideo()" [disabled]="!canGoToNextVideo()"
                        aria-label="Next draft"
                        class="absolute right-2 top-1/2 z-20 flex size-10 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-white/10 text-white/90 shadow-sm backdrop-blur-md transition-all duration-150 hover:bg-white/20 active:bg-white/15 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white/50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white/10">
                        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2"
                            stroke="currentColor" aria-hidden="true" class="size-5">
                            <path stroke-linecap="round" stroke-linejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
                        </svg>
                    </button>
                    @if (currentVideoIndex() >= 0) {
                    <p
                        class="pointer-events-none absolute bottom-3 left-1/2 z-20 m-0 -translate-x-1/2 rounded-full border border-white/20 bg-black/40 px-3 py-1 text-xs font-medium text-white/90 backdrop-blur-md">
                        {{ currentVideoIndex() + 1 }} / {{ videos.length }}
                    </p>
                    }
                    }
                    <div class="w-full h-full flex flex-col items-center justify-center">
                        @if (video) {
                        <atom-video-with-controls [video]="video" [videoFormat]="videoFormat"
                            class="h-[60vh] sm:h-[90%] max-w-[95%] sm:max-w-[80%] flex items-center justify-center"
                            [class]="videoFormat !== null && videoFormat === 1 ? 'aspect-[16/9]' : 'aspect-[9/16]'">
                        </atom-video-with-controls>
                        }
                    </div>
                </article>

                <!-- ── Right: Info panel ──────────────────────────────────── -->
                <article
                    class="col-span-2 rounded-2xl border border-white/10 dark:border-white/5 h-full bg-white dark:bg-zinc-900 overflow-hidden flex flex-col shadow-xl shadow-black/20">

                    <!-- ── Panel header ──────────────────────────────────── -->
                    <header class="sticky top-0 z-10 drawer-header-primary shrink-0 flex flex-col">

                        @if (video) {
                        <!-- Creator row -->
                        <div class="px-3 pt-3 pb-2 flex items-center justify-between gap-2">
                            <div class="flex items-center gap-2.5 min-w-0">
                                <div class="size-9 shrink-0 rounded-full overflow-hidden ring-2 ring-white/10">
                                    <atom-ldg-img
                                        [src]="creator.photoURL ? imageService.getImageSource(creator.photoURL, 'gallery') : 'https://ionicframework.com/docs/img/demos/card-media.png'"
                                        h="full" w="full" radius="full">
                                    </atom-ldg-img>
                                </div>
                                <div class="min-w-0">
                                    <p class="text-sm font-semibold text-primary truncate leading-tight">
                                        {{ creator.firstName }} {{ creator.lastName.charAt(0) }}.
                                    </p>
                                    <p class="m-0 text-[10px] text-secondary leading-tight">
                                        {{ formatService.getTimeSinceUpdated(video.createdDate) }}
                                    </p>
                                    @if (creatorId()) {
                                    <atom-copy-id class="mt-0.5" [value]="creatorId()" label="Creator ID" />
                                    }
                                </div>
                            </div>

                            <!-- Approve / approved / loading -->
                            @if (canApproveVideo(video)) {
                            @if (approvalLoading()) { <!-- Cleaner inline spinner -->
                            <div class="size-7 flex items-center justify-center shrink-0">
                                <svg class="animate-spin size-5 text-accent" viewBox="0 0 24 24" fill="none"
                                    aria-hidden="true">
                                    <circle class="opacity-20" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="3" />
                                    <path class="opacity-80" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                            } @else {
                            <button (click)="triggerVideoApproval(video)" [appTooltip]="'Approve Video'" position="top"
                                type="button"
                                class="primary-button-success inline-flex items-center justify-center gap-x-1.5 shrink-0">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="size-4">
                                    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
                                    <path d="m9 11 3 3L22 4" />
                                </svg>
                                Approve
                            </button>
                            }
                            } @else if (approvalBlockedReason(video)) {
                            <!-- Deliberately not `disabled`: the button stays focusable and clickable so
                                     it can explain why approval is unavailable. -->
                            <button (click)="explainApprovalBlocked(video)"
                                [appTooltip]="approvalBlockedReason(video) || ''" position="top" type="button"
                                aria-disabled="true"
                                class="primary-button-success inline-flex items-center justify-center gap-x-1.5 shrink-0 opacity-40">
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="size-4">
                                    <path d="M21.801 10A10 10 0 1 1 17 3.335" />
                                    <path d="m9 11 3 3L22 4" />
                                </svg>
                                Approve
                            </button>
                            }

                            @if (video.status === VideoStatusEnum.Accepted) {
                            <span
                                class="inline-flex items-center gap-x-1 rounded-full bg-emerald-400/10 px-2.5 py-1 text-xs font-medium text-emerald-400 ring-1 ring-inset ring-emerald-400/20 shrink-0">
                                <svg viewBox="0 0 6 6" aria-hidden="true" class="size-1.5 fill-emerald-400">
                                    <circle r="3" cx="3" cy="3" />
                                </svg>
                                Approved
                            </span>
                            }
                        </div>

                        <!-- Video metadata + download -->
                        <div
                            class="px-3 pb-2 flex items-center justify-between gap-3 border-b border-zinc-100 dark:border-white/5">
                            <div class="flex flex-col gap-0.5 min-w-0">
                                <p class="text-[10px] text-secondary truncate">
                                    <span class="text-zinc-400 dark:text-zinc-500">Draft: </span>
                                    <span class="font-medium text-primary">{{ video.videoName }}</span>
                                </p>
                                @if (video.aiModerateScore !== null) {
                                <p class="text-[10px] text-secondary">
                                    <span class="text-zinc-400 dark:text-zinc-500">Accuracy: </span>
                                    <span class="font-semibold text-primary tabular-nums">{{ video.aiModerateScore
                                        }}%</span>
                                </p>
                                }
                            </div>

                            @if (video.status === VideoStatusEnum.Accepted || currentUserService.hasAnyRole([4])) {
                            @if (uploadService.isDownloading()) {
                            <div class="size-7 flex items-center justify-center shrink-0">
                                <svg class="animate-spin size-5 text-accent" viewBox="0 0 24 24" fill="none"
                                    aria-hidden="true">
                                    <circle class="opacity-20" cx="12" cy="12" r="10" stroke="currentColor"
                                        stroke-width="3" />
                                    <path class="opacity-80" fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                </svg>
                            </div>
                            } @else {
                            <button type="button" (click)="downloadContent()"
                                class="inline-flex items-center gap-x-1.5 secondary-button-full-small shrink-0">
                                Download
                                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                    stroke-linejoin="round" class="size-3.5">
                                    <path d="M12 15V3" />
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                                    <path d="m7 10 5 5 5-5" />
                                </svg>
                            </button>
                            }
                            } @else {
                            <p class="text-[10px] text-zinc-400 dark:text-zinc-500 italic shrink-0">Downloadable once
                                approved</p>
                            }
                        </div>
                        }

                        <!-- Ad License panel -->
                        @if (video && currentLicenseSlot() !== 'hidden') {
                        <div *appHasRole="[77]" class="px-3 py-2 flex flex-col gap-2 border-b border-zinc-100 dark:border-white/5"
                            data-testid="player-license-panel">
                            <div class="flex items-center justify-between text-xs">
                                <span class="text-secondary">Usage rights</span>
                                <span class="font-bold text-primary">{{ usageRightsLabel() }}</span>
                            </div>
                            @switch (currentLicenseSlot()) {
                            @case ('pending') {
                            <div class="rounded-lg bg-[#E7E6EA] dark:bg-zinc-800 p-3 flex flex-col gap-1">
                                <span class="text-sm font-extrabold text-[#2B2340] dark:text-white">License pending creator
                                    approval</span>
                                <span class="text-xs font-semibold leading-tight text-[#58516A] dark:text-zinc-300">Your {{
                                    currentLicense()?.termDays }} days start when {{ creator.firstName }} approves. {{
                                    pendingLicensePrice() | currency: licenseCurrency() }} is refunded to your wallet if they
                                    decline.</span>
                            </div>
                            }
                            @case ('licensed') {
                            <div class="rounded-lg bg-[#F0E6FE] dark:bg-violet-900/30 p-3 flex flex-col gap-1">
                                <span class="text-sm font-extrabold text-[#7421FB] dark:text-violet-300">Ad Licensed until {{
                                    currentLicense()?.expiresAt | date: 'd MMM yyyy' }}</span>
                                <span class="text-xs font-semibold leading-tight text-[#58516A] dark:text-zinc-300">Cleared for
                                    boosting on your channels and dark ads.</span>
                            </div>
                            }
                            @case ('locked') {
                            <div
                                class="rounded-lg border border-dashed border-black/14 dark:border-white/20 p-3 text-xs font-semibold text-secondary">
                                Approve this draft to unlock an Ad License.</div>
                            }
                            @default {
                            <!-- canBuy / expired -->
                            <div class="rounded-xl p-4 flex flex-col gap-2.5 text-white"
                                style="background: linear-gradient(135deg, #12062B 0%, #6508FB 100%)">
                                <span class="text-[11px] font-extrabold uppercase tracking-[0.08em] opacity-85">Ad License</span>
                                <div class="flex items-baseline gap-2">
                                    <span class="text-3xl font-extrabold leading-[0.8] tracking-[-0.02em] tabular-nums">{{
                                        quotePrice() | currency: licenseCurrency() }}</span>
                                    <span class="text-sm font-bold opacity-85">for {{ licenseQuote()?.termDays }} days</span>
                                </div>
                                @if (currentLicenseSlot() === 'expired') {
                                <span class="text-xs font-semibold opacity-85">Previous licence expired {{
                                    currentLicense()?.expiresAt | date: 'd MMM yyyy' }}.</span>
                                }
                                <span class="text-sm font-semibold leading-tight">Boost this video on your own channels or run
                                    it as a dark ad.</span>
                                <button type="button" (click)="onBuyLicense()"
                                    class="h-10 rounded-lg bg-white text-sm font-extrabold text-[#6508FB] hover:bg-[#F0E6FE] active:scale-[0.98] transition">Buy
                                    Ad License</button>
                            </div>
                            }
                            }
                        </div>
                        }

                        <!-- ── Tab navigation ────────────────────────────── -->
                        <div class="hidden sm:block px-1">
                            <nav aria-label="Tabs" class="flex flex-row w-full justify-center space-x-6 cursor-pointer">

                                <a (click)="setSelectedTab('feedback')" aria-current="page"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('feedback')">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('feedback')" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
                                    </svg>
                                    <span class="whitespace-nowrap">Feedback</span>
                                </a>

                                <a (click)="setSelectedTab('client-review')"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('client-review')"
                                    [attr.aria-current]="selectedTab() === 'client-review' ? 'page' : null">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('client-review')" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                    </svg>
                                    <span class="whitespace-nowrap">Client Review</span>
                                    <span
                                        class="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-600 ring-1 ring-inset ring-violet-500/20 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-400/20">
                                        Beta
                                    </span>
                                    @if (isClientReviewApproved()) {
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-4 shrink-0 text-green-500"
                                        aria-hidden="true">
                                        <path d="M20 6 9 17l-5-5" />
                                    </svg>
                                    <span class="sr-only">Approved</span>
                                    } @else if (isClientReviewDeclined()) {
                                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-4 shrink-0 text-red-500"
                                        aria-hidden="true">
                                        <path d="M18 6 6 18" />
                                        <path d="m6 6 12 12" />
                                    </svg>
                                    <span class="sr-only">Declined</span>
                                    }
                                </a>

                                @if (canViewPostDetails()) {
                                <a (click)="setSelectedTab('details')"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('details')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('details')" aria-hidden="true">
                                        <path
                                            d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                                        <rect x="2" y="6" width="14" height="12" rx="2" />
                                    </svg>
                                    <span class="whitespace-nowrap">Details</span>
                                </a>
                                }

                                @if (videoAiModerateService.noodleVisible().video_feedback ||
                                currentUserService.hasAnyRole([77])) {
                                <a (click)="setSelectedTab('analysis')" aria-current="page"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('analysis')">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('analysis')" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                    </svg>
                                    <span class="whitespace-nowrap">Accuracy</span>
                                </a>
                                }

                                @if (showsResultsTab()) {
                                <a (click)="setSelectedTab('results')" aria-current="page"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('results')">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('results')" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 0 1 3 19.875v-6.75ZM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V8.625ZM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 0 1-1.125-1.125V4.125Z" />
                                    </svg>
                                    <span class="whitespace-nowrap">Results</span>
                                </a>

                                @if (ttoResults) {
                                <a (click)="setSelectedTab('tto')" aria-current="page"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('tto')">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('tto')" aria-hidden="true">
                                        <path d="M3 3v16a2 2 0 0 0 2 2h16" />
                                        <path
                                            d="M7 11.207a.5.5 0 0 1 .146-.353l2-2a.5.5 0 0 1 .708 0l3.292 3.292a.5.5 0 0 0 .708 0l4.292-4.292a.5.5 0 0 1 .854.353V16a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1z" />
                                    </svg>
                                    <span class="whitespace-nowrap">TT Insights</span>
                                </a>
                                }

                                <!-- <a (click)="setSelectedTab('comments')" aria-current="page"
                                    class="group inline-flex items-center gap-x-1.5 border-b-2 px-1 py-2.5 text-sm font-medium transition-all duration-200 ease-in-out"
                                    [class]="getSelectedTabClass('comments')">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" class="size-4 shrink-0"
                                        [class]="getSelectedTabIconClass('comments')" aria-hidden="true">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M12 20.25c4.97 0 9-3.694 9-8.25s-4.03-8.25-9-8.25S3 7.444 3 12c0 2.104.859 4.023 2.273 5.48.432.447.74 1.04.586 1.641a4.483 4.483 0 0 1-.923 1.785A5.969 5.969 0 0 0 6 21c1.282 0 2.47-.402 3.445-1.087.81.22 1.668.337 2.555.337Z" />
                                    </svg>
                                    <span class="whitespace-nowrap">Post Comments</span>
                                </a> -->
                                }

                            </nav>
                        </div>

                    </header>

                    <!-- ── Panel body ────────────────────────────────────── -->
                    <main class="flex-1 flex-col overflow-hidden drawer-body-primary">
                        @switch (selectedTab()) {

                        @case ('feedback') {
                        @if (videoFeedback() && videoFeedback()!.length > 0) {
                        <div class="h-full overflow-y-auto flow-root px-4 py-3 scrollbar-transparent">
                            <ul role="list" class="-mb-6">
                                @for (feedback of videoFeedback()!; track $index) {
                                <li [id]="feedback.id" class="rounded-xl transition-colors duration-500"
                                    [class]="highlightItem() && highlightItem() == feedback.id ? 'backgroundColourFade animated4000' : 'bg-transparent'">
                                    <div class="relative pb-6">
                                        @if ($index < videoFeedback()!.length - 1) { <span aria-hidden="true"
                                            class="absolute left-4 top-4 -ml-px h-full w-px bg-zinc-100 dark:bg-zinc-800">
                                            </span>
                                            }
                                            <div class="relative flex items-start gap-3">
                                                <!-- Avatar -->
                                                <div class="relative shrink-0">
                                                    <img [src]="imageService.getImageSource(feedback.userCreated ? feedback.userCreated.photoURL : feedback.creatorCreated ? feedback.creatorCreated.photoURL : 'https://ionicframework.com/docs/img/demos/card-media.png', 'gallery')"
                                                        alt=""
                                                        class="size-8 rounded-full object-cover bg-zinc-200 dark:bg-zinc-700 ring-2 ring-white dark:ring-zinc-900" />
                                                    <!-- Role badge -->
                                                    <span
                                                        class="absolute -bottom-0.5 -right-1 flex items-center justify-center size-4 rounded-full bg-white dark:bg-zinc-900 ring-1 ring-zinc-100 dark:ring-zinc-800">
                                                        @if (feedback.userCreated) {
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                            fill="currentColor" class="size-3 text-zinc-400">
                                                            <path fill-rule="evenodd"
                                                                d="M3 2.25a.75.75 0 0 0 0 1.5v16.5h-.75a.75.75 0 0 0 0 1.5H15v-18a.75.75 0 0 0 0-1.5H3ZM6.75 19.5v-2.25a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75v2.25a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1-.75-.75ZM6 6.75A.75.75 0 0 1 6.75 6h.75a.75.75 0 0 1 0 1.5h-.75A.75.75 0 0 1 6 6.75ZM6.75 9a.75.75 0 0 0 0 1.5h.75a.75.75 0 0 0 0-1.5h-.75ZM6 12.75a.75.75 0 0 1 .75-.75h.75a.75.75 0 0 1 0 1.5h-.75a.75.75 0 0 1-.75-.75ZM10.5 6a.75.75 0 0 0 0 1.5h.75a.75.75 0 0 0 0-1.5h-.75Zm-.75 3.75A.75.75 0 0 1 10.5 9h.75a.75.75 0 0 1 0 1.5h-.75a.75.75 0 0 1-.75-.75ZM10.5 12a.75.75 0 0 0 0 1.5h.75a.75.75 0 0 0 0-1.5h-.75ZM16.5 6.75v15h5.25a.75.75 0 0 0 0-1.5H21v-12a.75.75 0 0 0 0-1.5h-4.5Zm1.5 4.5a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75h-.008a.75.75 0 0 1-.75-.75v-.008Zm.75 2.25a.75.75 0 0 0-.75.75v.008c0 .414.336.75.75.75h.008a.75.75 0 0 0 .75-.75v-.008a.75.75 0 0 0-.75-.75h-.008ZM18 17.25a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75h-.008a.75.75 0 0 1-.75-.75v-.008Z"
                                                                clip-rule="evenodd" />
                                                        </svg>
                                                        } @else if (feedback.creatorCreated) {
                                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                                                            fill="currentColor" class="size-3 text-zinc-400">
                                                            <path fill-rule="evenodd"
                                                                d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z"
                                                                clip-rule="evenodd" />
                                                        </svg>
                                                        }
                                                    </span>
                                                </div>

                                                <!-- Content -->
                                                <div class="min-w-0 flex-1">
                                                    <div class="flex items-start justify-between gap-2">
                                                        <div>
                                                            <p class="text-xs font-semibold text-primary leading-tight">
                                                                @if (feedback.userCreated) {
                                                                {{ feedback.userCreated.firstName }} {{
                                                                feedback.userCreated.lastName }}
                                                                } @else {
                                                                {{ feedback.creatorCreated.firstName }} {{
                                                                feedback.creatorCreated.lastName }}
                                                                }
                                                            </p>
                                                            <p class="text-[10px] text-secondary leading-tight">
                                                                {{
                                                                formatService.getTimeSinceUpdated(feedback.createdDate)
                                                                }}
                                                            </p>
                                                        </div>
                                                        @if (!haveIAlreadySeenThisFeedback(feedback)) {
                                                        <span
                                                            class="inline-flex items-center gap-x-1 rounded-full bg-primary/10 dark:bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-accent ring-1 ring-inset ring-primary/20 shrink-0">
                                                            <svg viewBox="0 0 6 6" aria-hidden="true"
                                                                class="size-1.5 fill-primary">
                                                                <circle r="3" cx="3" cy="3" />
                                                            </svg>
                                                            New
                                                        </span>
                                                        }
                                                    </div>
                                                    <div class="mt-1 text-xs text-primary leading-relaxed">
                                                        @if (feedback.feedbackHtml && feedback.feedbackHtml.length > 0)
                                                        {
                                                        <div [innerHTML]="feedback.feedbackHtml"></div>
                                                        } @else if (feedbackIsHtml(feedback.feedback)) {
                                                        <div [innerHTML]="feedback.feedback"></div>
                                                        } @else {
                                                        <p>{{ feedback.feedback }}</p>
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                    </div>
                                </li>
                                }
                            </ul>
                        </div>

                        } @else if (videoFeedback() && videoFeedback()!.length == 0) {
                        <div class="flex flex-col items-center justify-center h-full px-8 py-10 text-center">
                            <div
                                class="flex items-center justify-center size-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 mb-5 ring-1 ring-inset ring-violet-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" aria-hidden="true"
                                    class="size-8 text-violet-500 dark:text-violet-400">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M8.625 12a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H8.25m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0H12m4.125 0a.375.375 0 1 1-.75 0 .375.375 0 0 1 .75 0Zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 0 1-2.555-.337A5.972 5.972 0 0 1 5.41 20.97a5.969 5.969 0 0 1-.474-.065 4.48 4.48 0 0 0 .978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25Z" />
                                </svg>
                            </div>
                            <h3 class="text-base font-semibold text-primary">Start the feedback thread</h3>
                            <p class="mt-2 text-sm text-secondary max-w-sm">
                                Share revision notes, approvals, or creative direction below. The creator is notified
                                and can reply in this thread — keep brief chat for general conversation.
                            </p>
                        </div>

                        } @else {
                        <atom-loading-inline title="Loading" message="Loading feedback..."></atom-loading-inline>
                        }
                        }

                        @case ('details') {
                        @if (canViewPostDetails()) {
                        <div class="h-full overflow-y-auto px-4 py-3 scrollbar-transparent">
                            @if (detailsLoadingState() === 'loading' || detailsLoadingState() === 'idle') {
                            <atom-loading-inline title="Loading"
                                message="Loading post details..."></atom-loading-inline>
                            } @else if (detailsLoadingState() === 'error') {
                            <div class="flex flex-col items-center justify-center h-full px-8 py-10 text-center">
                                <div
                                    class="flex items-center justify-center size-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 mb-5 ring-1 ring-inset ring-violet-500/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-8 text-violet-500 dark:text-violet-400"
                                        aria-hidden="true">
                                        <path
                                            d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                                        <rect x="2" y="6" width="14" height="12" rx="2" />
                                    </svg>
                                </div>
                                <h3 class="text-base font-semibold text-primary">Could not load post details</h3>
                                <p class="mt-2 text-sm text-secondary max-w-sm">
                                    Something went wrong while loading the video link and ad code. Try again.
                                </p>
                                <button type="button" (click)="loadSubmittedPostDetails()"
                                    class="primary-button mt-6 text-sm">
                                    Retry
                                </button>
                            </div>
                            } @else if (submittedPosts() && submittedPosts()!.length > 0) {
                            <div class="space-y-3">
                                @for (post of submittedPosts()!; track post.id) {
                                <section
                                    class="rounded-xl border border-border bg-card p-4 shadow-sm shadow-black/5 dark:shadow-black/20">
                                    <h3 class="m-0 text-sm font-semibold text-primary">
                                        {{ getSocialChannelLabel(post.socialChannel) }}
                                    </h3>
                                    <p class="mt-0.5 text-xs text-secondary">
                                        Video link and ad code for this live post.
                                    </p>
                                    <ipr-submitted-post-fields [post]="post"
                                        (saved)="onSubmittedPostDetailsSaved($event)">
                                    </ipr-submitted-post-fields>
                                </section>
                                }
                            </div>
                            } @else {
                            <div class="flex flex-col items-center justify-center h-full px-8 py-10 text-center">
                                <div
                                    class="flex items-center justify-center size-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 mb-5 ring-1 ring-inset ring-violet-500/20">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-8 text-violet-500 dark:text-violet-400"
                                        aria-hidden="true">
                                        <path
                                            d="m16 13 5.223 3.482a.5.5 0 0 0 .777-.416V7.87a.5.5 0 0 0-.752-.432L16 10.5" />
                                        <rect x="2" y="6" width="14" height="12" rx="2" />
                                    </svg>
                                </div>
                                <h3 class="text-base font-semibold text-primary">No live post submitted yet</h3>
                                <p class="mt-2 text-sm text-secondary max-w-sm">
                                    The video link and ad code will appear here once the creator submits their live
                                    post.
                                </p>
                            </div>
                            }
                        </div>
                        }
                        }

                        @case ('analysis') {
                        @if (reviewHistory() && reviewHistory()!.reviewResults.length > 0) {
                        <atom-individual-post-ai-review [reviewHistory]="reviewHistory()"
                            (sendSuggestedFeedbackEvent)="sendSuggestedFeedback($event)">
                        </atom-individual-post-ai-review>
                        } @else {
                        <div class="flex flex-col items-center justify-center h-full px-8 py-10 text-center">
                            <div
                                class="flex items-center justify-center size-16 rounded-2xl bg-violet-50 dark:bg-violet-500/10 mb-5 ring-1 ring-inset ring-violet-500/20">
                                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                    stroke-width="1.5" stroke="currentColor" aria-hidden="true"
                                    class="size-8 text-violet-500 dark:text-violet-400">
                                    <path stroke-linecap="round" stroke-linejoin="round"
                                        d="M9.813 15.904 9 18.75l-.813-2.846a4.5 4.5 0 0 0-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 0 0 3.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 0 0 3.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 0 0-3.09 3.09ZM18.259 8.715 18 9.75l-.259-1.035a3.375 3.375 0 0 0-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 0 0 2.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 0 0 2.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 0 0-2.456 2.456ZM16.894 20.567 16.5 21.75l-.394-1.183a2.25 2.25 0 0 0-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 0 0 1.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 0 0 1.423 1.423l1.183.394-1.183.394a2.25 2.25 0 0 0-1.423 1.423Z" />
                                </svg>
                            </div>
                            <h3 class="text-base font-semibold text-primary">No AI analysis yet</h3>
                            <p class="mt-2 text-sm text-secondary max-w-sm">
                                AI analysis checks this draft against your brief requirements and flags issues to fix.
                                Run it now, or wait for an automatic review to finish.
                            </p>
                            <div class="mt-6">
                                @if (!reRunNoodleLoading()) {
                                <button type="button" (click)="reRunReview()"
                                    class="primary-button text-sm inline-flex flex-row items-center gap-x-1.5">
                                    Run Analysis
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                        stroke-linejoin="round" class="size-5" aria-hidden="true">
                                        <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                                        <path d="M21 3v5h-5" />
                                        <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                                        <path d="M8 16H3v5" />
                                    </svg>
                                </button>
                                } @else {
                                <span
                                    class="inline-flex items-center gap-x-1.5 rounded-md px-2 py-1 text-sm font-medium text-gray-900 inset-ring inset-ring-gray-200 dark:text-white dark:inset-ring-white/10">
                                    <svg aria-hidden="true"
                                        class="mx-auto w-5 h-5 text-gray-200 animate-spin dark:text-gray-300 fill-primary"
                                        viewBox="0 0 100 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                                            fill="currentColor" />
                                        <path
                                            d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                                            fill="currentFill" />
                                    </svg>

                                    Submitting for Analysis...
                                </span>
                                }
                            </div>
                        </div>
                        }
                        }

                        @case ('client-review') {
                        <div class="h-full overflow-y-auto px-4 py-6">
                            @if (isInClientReview()) {
                            <article
                                class="mx-auto w-full max-w-lg overflow-hidden rounded-2xl border border-zinc-200 bg-white dark:border-zinc-700 dark:bg-zinc-900">
                                <header
                                    class="flex flex-col gap-4 border-b border-zinc-100 px-5 py-4 dark:border-zinc-800">
                                    <span
                                        [class]="'inline-flex w-fit items-center gap-x-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset ' + clientReviewTone()">
                                        <svg viewBox="0 0 6 6" aria-hidden="true"
                                            [class]="'size-1.5 ' + clientReviewDotTone()">
                                            <circle r="3" cx="3" cy="3" />
                                        </svg>
                                        {{ clientReviewLabel() }}
                                    </span>
                                    <dl class="grid grid-cols-2 gap-3">
                                        <div>
                                            <dt class="text-xs text-secondary">Sent</dt>
                                            <dd class="mt-0.5 text-sm font-medium text-primary">
                                                @if (clientReviewState()?.sentToClientAt) {
                                                {{ clientReviewState()?.sentToClientAt | date:'d MMM y, HH:mm' }}
                                                } @else {
                                                —
                                                }
                                            </dd>
                                        </div>
                                        <div>
                                            <dt class="text-xs text-secondary">Reviewed</dt>
                                            <dd class="mt-0.5 text-sm font-medium text-primary">
                                                @if (clientReviewState()?.reviewedAt) {
                                                {{ clientReviewState()?.reviewedAt | date:'d MMM y, HH:mm' }}
                                                } @else {
                                                Waiting
                                                }
                                            </dd>
                                        </div>
                                    </dl>
                                </header>

                                <section class="px-5 py-5" aria-labelledby="client-review-feedback-heading">
                                    <h3 id="client-review-feedback-heading"
                                        class="text-xs font-medium uppercase tracking-wide text-secondary">
                                        Client feedback
                                    </h3>
                                    @if (hasClientFeedback()) {
                                    <blockquote
                                        class="mt-3 rounded-xl bg-zinc-50 px-4 py-3 text-sm leading-relaxed text-primary dark:bg-zinc-800/80">
                                        <p class="whitespace-pre-wrap">{{ clientReviewFeedback() }}</p>
                                    </blockquote>
                                    } @else {
                                    <p class="mt-3 text-sm text-secondary">
                                        No comments yet. The client can still approve, decline, or leave
                                        notes from the review link.
                                    </p>
                                    }
                                </section>

                                @if (clientReviewShareUrl()) {
                                <section
                                    class="border-t border-zinc-100 px-5 py-4 dark:border-zinc-800"
                                    aria-labelledby="client-review-link-heading">
                                    <div class="flex items-start justify-between gap-3">
                                        <div class="min-w-0">
                                            <h3 id="client-review-link-heading"
                                                class="text-xs font-medium uppercase tracking-wide text-secondary">
                                                Client review link
                                            </h3>
                                            <p class="mt-1 text-sm text-secondary">
                                                @if (clientReviewShareHost()) {
                                                {{ clientReviewShareHost() }}
                                                } @else {
                                                Public draft review
                                                }
                                            </p>
                                        </div>
                                        <div class="flex shrink-0 items-center gap-2">
                                            <button type="button" (click)="copyClientReviewLink()"
                                                class="secondary-button-full-small inline-flex items-center gap-x-1.5"
                                                [attr.aria-label]="clientReviewLinkCopied() ? 'Copied client review link' : 'Copy client review link'">
                                                @if (clientReviewLinkCopied()) {
                                                Copied
                                                } @else {
                                                Copy
                                                }
                                            </button>
                                            <button type="button" (click)="openClientReview()"
                                                class="primary-button inline-flex items-center gap-x-1.5">
                                                Open
                                            </button>
                                        </div>
                                    </div>
                                    <p
                                        class="mt-3 break-all rounded-lg bg-zinc-50 px-3 py-2 font-mono text-xs text-secondary dark:bg-zinc-800/80">
                                        {{ clientReviewSharePath() }}
                                    </p>
                                </section>
                                }
                            </article>
                            } @else {
                            <div class="flex h-full flex-col items-center justify-center px-8 py-10 text-center">
                                <div
                                    class="mb-5 flex size-16 items-center justify-center rounded-2xl bg-violet-50 ring-1 ring-inset ring-violet-500/20 dark:bg-violet-500/10">
                                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                        stroke-width="1.5" stroke="currentColor" aria-hidden="true"
                                        class="size-8 text-violet-500 dark:text-violet-400">
                                        <path stroke-linecap="round" stroke-linejoin="round"
                                            d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
                                    </svg>
                                </div>
                                <div class="flex items-center justify-center gap-2">
                                    <h3 class="text-base font-semibold text-primary">Not sent to client review</h3>
                                    <span
                                        class="inline-flex items-center rounded-full bg-violet-50 px-1.5 py-0.5 text-xs font-medium text-violet-600 ring-1 ring-inset ring-violet-500/20 dark:bg-violet-500/10 dark:text-violet-400 dark:ring-violet-400/20">
                                        Beta
                                    </span>
                                </div>
                                <p class="mt-2 max-w-sm text-sm text-secondary">
                                    Send this draft to client review to generate the public review link.
                                    Client feedback will show here after they respond.
                                </p>
                                <div class="mt-6">
                                    @if (pickingPostSlot()) {
                                    <div class="flex flex-col items-center gap-2">
                                        <p class="text-xs text-secondary">Which post is this draft for?</p>
                                        <div class="flex flex-wrap items-center justify-center gap-2">
                                            @for (slot of postSlots(); track slot) {
                                            <button type="button"
                                                class="primary-button inline-flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
                                                [disabled]="sendingToClientReview()"
                                                (click)="sendToClientReview(slot)">
                                                Post {{ slot }}
                                            </button>
                                            }
                                            <button type="button" class="secondary-button-full-small"
                                                [disabled]="sendingToClientReview()"
                                                (click)="cancelPostPick()">
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                    } @else {
                                    <button (click)="onSendToClientReviewClick()" type="button"
                                        [appTooltip]="'Send this draft to the client for review'"
                                        position="top" [disabled]="sendingToClientReview()"
                                        [attr.aria-busy]="sendingToClientReview() ? 'true' : null"
                                        class="primary-button inline-flex items-center justify-center gap-x-1.5 disabled:opacity-60 disabled:cursor-not-allowed">
                                        @if (sendingToClientReview()) {
                                        <svg class="animate-spin size-4" viewBox="0 0 24 24" fill="none"
                                            aria-hidden="true">
                                            <circle class="opacity-20" cx="12" cy="12" r="10"
                                                stroke="currentColor" stroke-width="3" />
                                            <path class="opacity-80" fill="currentColor"
                                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                        </svg>
                                        Sending...
                                        } @else {
                                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"
                                            stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                            stroke-linejoin="round" class="size-4" aria-hidden="true">
                                            <path d="m22 2-7 20-4-9-9-4Z" />
                                            <path d="M22 2 11 13" />
                                        </svg>
                                        Send to Client Review
                                        }
                                    </button>
                                    }
                                </div>
                            </div>
                            }
                        </div>
                        }

                        @case ('results') {
                        <atom-individual-post-results [applicationId]="briefApplication?.id ?? undefined"
                            [video]="video" [creator]="creator"
                            [primarySocialChannel]="resultsPrimaryChannel()"
                            [briefCrossPlatforms]="resultsCrossPlatforms()"
                            [suggestedHashtags]="resultsHashtags()"
                            [callToActionLink]="resultsCallToAction()">
                        </atom-individual-post-results>
                        }
                        @case ('tto') {
                        <atom-individual-post-results-tto>
                        </atom-individual-post-results-tto>
                        }

                        @case ('comments') {
                        <atom-individual-post-comments [applicationId]="briefApplication?.id ?? undefined"
                            [video]="video" [creator]="creator">
                        </atom-individual-post-comments>
                        }

                        }
                    </main>

                    <!-- ── Panel footer ──────────────────────────────────── -->
                    @switch (selectedTab()) {
                    @case ('feedback') {
                    <div class="px-3 py-2.5 drawer-header-primary">
                        <div class="flex flex-row w-full items-start gap-2.5">
                            @if (currentUserService.shoutUser$ | async; as shoutUser) {
                            @if (shoutUser?.photoURL) {
                            <img [src]="imageService.getImageSource(shoutUser.photoURL, 'gallery')" alt=""
                                class="size-7 flex-none rounded-full object-cover ring-2 ring-white/10 mt-0.5" />
                            }
                            }
                            <!-- <div class="relative flex-auto bg-[var(--input)] rounded-xl">


                                <div class="overflow-hidden rounded-xl border-2 border-blue-500">
                                    <atom-wysiswyg #videoFeedbackWysiwyg
                                        (htmlContentChange)="onHtmlContentChange($event)" [inputHtml]="feedbackComment"
                                        [placeholder]="'Add your comment...'"></atom-wysiswyg>
                                </div>
                                <div class="absolute inset-x-0 bottom-0 flex justify-end py-1.5 px-2">
                                    @if (!sendingFeedback()) {
                                    <button type="submit" (click)="addVideoFeedback()" [disabled]="sendingFeedback()"
                                        class="secondary-button text-primary text-xs">
                                        Comment
                                    </button>
                                    } @else {
                                    <p class="text-[10px] text-secondary self-center">Sending…</p>
                                    }
                                </div>
                            </div> -->

                            <div class="flex-1 flex flex-col rounded-xl">


                                <div class="flex-1 overflow-hidden">
                                    <atom-wysiswyg #videoFeedbackWysiwyg
                                        (htmlContentChange)="onHtmlContentChange($event)" [inputHtml]="feedbackComment"
                                        [placeholder]="'Add your comment...'"></atom-wysiswyg>
                                </div>
                                <div class="shrink-0 flex flex-row items-center justify-end py-1.5">
                                    @if (!sendingFeedback()) {
                                    <button type="submit" (click)="addVideoFeedback()" [disabled]="sendingFeedback()"
                                        class="secondary-button text-primary text-xs inline-flex items-center gap-x-1.5">
                                        Comment
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                            viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
                                            stroke-linecap="round" stroke-linejoin="round" class="-mr-0.5 size-5">
                                            <path
                                                d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z" />
                                            <path d="m21.854 2.147-10.94 10.939" />
                                        </svg>
                                    </button>
                                    } @else {
                                    <p class="text-[10px] text-secondary self-center">Sending…</p>
                                    }
                                </div>
                            </div>
                        </div>
                    </div>
                    }
                    @case ('details') {}
                    @case ('analysis') {}
                    @case ('client-review') {}
                    @case ('results') {}
                    @case ('comments') {}
                    }

                </article>
            </div>
        </div>
    </div>
</div>